import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import SFToggleButton from "../../../toggleButton";
import ClickAwayListener from "@mui/base/ClickAwayListener";
import Collapse from "@mui/material/Collapse";
import Fade from "@mui/material/Fade";
import { SFSearchIcon } from "../../../icons";
import IconTypeTooltip from "../../components/iconTypeTooltip";
import ActionBarSearchInput from "./searchInput";
import getStyled from "../../../../hooks/getStyled";
import sfDatagridHelpers from "../../../dataGrid/helpers";

const { styled } = getStyled();

const CenteredFlexContainer = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const SearchAction = ({
  dropdownOptions = [],
  initialInput = "",
  initialSelection = [],
  onInputChange = () => {},
  onSelectionChange = () => {},
  gridName = "",
  autoSize,
  disableTooltip = false,
}) => {
  const { t } = useTranslation();
  // const[firstRender,SetFirstRender] = useState(true);
  const gridSearchTextFilterValue =
    sfDatagridHelpers.useSearchTextFilterValue(gridName);

  let initValue = gridName ? gridSearchTextFilterValue : initialInput;
  const [inputOpen, setInputOpen] = useState(
    !!gridSearchTextFilterValue || !!initialInput
  ); // The search input should be open if the input value is not empty
  const [inputValue, setInputValue] = useState(initValue); // Prioritize the grid search text filter value over the initial input value
  const [debouncedInputValue, setDebouncedInputValue] = useState(initValue);

  const [dropdownMenuOpen, setDropdownMenuOpen] = useState(false);
  const [selectedDropdownItems, setSelectedDropdownItems] =
    useState(initialSelection);

  useEffect(() => {
    const delayInputTimeoutId = setTimeout(() => {
      setDebouncedInputValue(inputValue);
    }, 400);
    return () => clearTimeout(delayInputTimeoutId);
  }, [inputValue, 400]);

  useEffect(() => {
    // Call the onInputChange function every time the debounced value changes
    // (after the user has stopped typing for 400ms)
    if (!debouncedInputValue || debouncedInputValue.trim().length > 2) {
      // The debounced value must have at least 3 characters after trimming to be considered valid
      // We also allow empty strings to be passed
      onInputChange(debouncedInputValue.trim());
    }
  }, [debouncedInputValue]);

  const handleInputValueChange = (event) => {
    setInputValue(event.target.value);
  };

  const handleClearInputValue = () => {
    // When the user clears the input value, hide the dropdown menu and collapse the input
    setInputValue("");
    setDropdownMenuOpen(false);
    setInputOpen(false);
  };

  const handleDropdownItemClick = (index) => {
    const clickedItem = dropdownOptions[index];
    if (selectedDropdownItems.includes(clickedItem)) {
      // Remove the clicked item from the selected items if it is already selected
      setSelectedDropdownItems((prev) => prev.filter((i) => i !== clickedItem));
      // Call the onSelectionChange function with the updated selected items array
      onSelectionChange(selectedDropdownItems.filter((i) => i !== clickedItem));
    } else {
      // Add the clicked item to the selected items if it is not already selected
      setSelectedDropdownItems((prev) => [...prev, clickedItem]);
      // Call the onSelectionChange function with the updated selected items array
      onSelectionChange([...selectedDropdownItems, clickedItem]);
    }
  };

  const handleDropdownMenuToggle = () => {
    setDropdownMenuOpen((prev) => !prev);
  };

  const handleClickAway = () => {
    if (inputOpen && inputValue.trim().length === 0) {
      // When the user clicks away from the search input and the input value is empty,
      // collapse the input
      setInputOpen(false);
    }
    if (dropdownMenuOpen) {
      // When the user clicks away from the search input,
      // hide the dropdown menu
      setDropdownMenuOpen(false);
    }
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <CenteredFlexContainer>
        <Fade in={!inputOpen} appear={false} exit={false} timeout={500}>
          <SFToggleButton
            centerRipple
            onClick={() => {
              setInputOpen((prev) => !prev);
            }}
            value="search"
            ariaLabel={t("components.actionBar.tooltips.search")}
          >
            {!inputOpen && (
              <IconTypeTooltip
                tooltip={t("components.actionBar.tooltips.search")}
                disableTooltip={disableTooltip}
              >
                <CenteredFlexContainer>
                  <SFSearchIcon />
                </CenteredFlexContainer>
              </IconTypeTooltip>
            )}
          </SFToggleButton>
        </Fade>
        <Collapse orientation="horizontal" in={inputOpen} enter={false}>
          <ActionBarSearchInput
            inputValue={inputValue}
            onChange={handleInputValueChange}
            onClear={handleClearInputValue}
            dropdownMenuOpen={dropdownMenuOpen}
            onDropdownMenuToggle={handleDropdownMenuToggle}
            dropdownOptions={dropdownOptions}
            selectedDropdownOptions={selectedDropdownItems}
            onDropdownOptionClick={handleDropdownItemClick}
            autoSize={autoSize}
          />
        </Collapse>
      </CenteredFlexContainer>
    </ClickAwayListener>
  );
};

export default SearchAction;

SearchAction.propTypes = {
  /**
   * The options to display in the dropdown menu of the search field.
   */
  dropdownOptions: PropTypes.arrayOf(PropTypes.string),
  /**
   * The initial input value of the search field.
   */
  initialInput: PropTypes.string,
  /**
   * Allows pre-selecting options in the dropdown menu of the search field when it is first opened.
   */
  initialSelection: PropTypes.arrayOf(PropTypes.string),
  /**
   * The function to call when the input value changes. It receives the current input value as an argument.
   */
  onInputChange: PropTypes.func,
  /**
   * The function to call when any checkbox is selected or deselected in the dropdown menu of the search field.
   * It receives an array of the currently selected dropwdown options as an argument.
   */
  onSelectionChange: PropTypes.func,
  /**
   * The name of the grid to which the search action is associated.
   * Used for setting the initial input value of the search field based on the grid's search text filter value (`initialInput` is ignored if `gridName` is provided).
   */
  gridName: PropTypes.string,
};
